import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { API_BASE, getToken } from "@/lib/api";

const STATUSES = [
  "New", "Contacted", "Consultation Scheduled", "Quote Sent",
  "Awaiting Retainer", "Confirmed", "Shoot Completed", "Editing",
  "Delivered", "Cancelled",
];

export default function AdminEnquiries() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/enquiries", { params: { status_filter: statusFilter, q } });
    setList(data || []);
  };

  useEffect(() => {
    load();
  }, [statusFilter]);

  const onSearch = (e) => {
    e.preventDefault();
    load();
  };

  const saveEnquiry = async () => {
    if (!selected) return;
    try {
      const { data } = await api.patch(`/admin/enquiries/${selected.id}`, {
        status: selected.status,
        internal_notes: selected.internal_notes,
        production_notes: selected.production_notes,
      });
      toast.success("Enquiry updated");
      setSelected(data);
      load();
    } catch {
      toast.error("Could not update enquiry");
    }
  };

  const del = async (e) => {
    if (!window.confirm(`Archive/delete ${e.reference}?`)) return;
    await api.delete(`/admin/enquiries/${e.id}`);
    toast.success("Deleted");
    setSelected(null);
    load();
  };

  const exportCsv = () => {
    // fetch protected CSV with token via anchor + fetch
    const url = `${API_BASE}/admin/enquiries/export.csv`;
    fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "oereshaus_enquiries.csv";
        link.click();
      });
  };

  return (
    <div data-testid="admin-enquiries">
      <p className="label-mono opacity-60">Bookings</p>
      <h1 className="mt-2 font-editorial text-5xl md:text-6xl">Enquiries</h1>

      <div className="mt-8 flex flex-wrap items-end gap-4">
        <form onSubmit={onSearch} className="flex items-end gap-3">
          <div>
            <label className="label-mono opacity-60">Search</label>
            <input value={q} onChange={(e) => setQ(e.target.value)} className="editorial-input" data-testid="enquiries-search" />
          </div>
          <button type="submit" className="btn-outline-ink">Search</button>
        </form>
        <div>
          <label className="label-mono opacity-60">Status</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="editorial-select" data-testid="enquiries-status-filter">
            <option value="all">All</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <button onClick={exportCsv} className="btn-outline-ink" data-testid="enquiries-export-csv">Export CSV</button>
      </div>

      <div className="mt-10 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-7 border border-ink/15 bg-white divide-y divide-ink/10 max-h-[70vh] overflow-y-auto">
          {list.length === 0 && (
            <p className="p-6 label-mono opacity-60" data-testid="enquiries-empty">No enquiries match.</p>
          )}
          {list.map((e) => (
            <button
              key={e.id}
              onClick={() => setSelected(e)}
              className={`w-full text-left p-4 hover:bg-ink/5 transition-colors ${selected?.id === e.id ? "bg-ink/5" : ""}`}
              data-testid={`enquiry-row-${e.reference}`}
            >
              <div className="flex items-baseline justify-between gap-4">
                <p className="font-editorial text-lg">{e.full_name}</p>
                <p className="label-mono opacity-60">{e.status}</p>
              </div>
              <p className="label-mono opacity-60 mt-1">
                {e.reference} · {e.service_category} · {e.selected_package || "no package"}
              </p>
              <p className="text-sm text-ink/70 mt-1">{e.email} · {e.mobile}</p>
            </button>
          ))}
        </div>

        <div className="col-span-12 md:col-span-5 border border-ink/15 bg-white p-6 min-h-[400px]">
          {selected ? (
            <div data-testid="enquiry-detail">
              <p className="label-mono opacity-60">{selected.reference}</p>
              <h2 className="mt-2 font-editorial text-3xl">{selected.full_name}</h2>
              <p className="label-mono opacity-70 mt-1">{selected.service_category} · {selected.selected_package || "—"}</p>

              <dl className="mt-6 space-y-3 text-sm">
                <div><dt className="label-mono opacity-60">Email</dt><dd>{selected.email}</dd></div>
                <div><dt className="label-mono opacity-60">Mobile</dt><dd>{selected.mobile}</dd></div>
                <div><dt className="label-mono opacity-60">Preferred date</dt><dd>{selected.preferred_date || "—"}</dd></div>
                <div><dt className="label-mono opacity-60">Alt date</dt><dd>{selected.alternative_date || "—"}</dd></div>
                <div><dt className="label-mono opacity-60">Venue</dt><dd>{selected.venue || "—"}</dd></div>
                <div><dt className="label-mono opacity-60">Coverage</dt><dd>{selected.photography_or_film}</dd></div>
                <div><dt className="label-mono opacity-60">Message</dt><dd className="whitespace-pre-wrap">{selected.additional_info || "—"}</dd></div>
              </dl>

              <div className="mt-6">
                <label className="label-mono opacity-60">Status</label>
                <select className="editorial-select" value={selected.status} onChange={(e) => setSelected({ ...selected, status: e.target.value })} data-testid="enquiry-status-select">
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="mt-4">
                <label className="label-mono opacity-60">Internal notes</label>
                <textarea rows={3} className="editorial-textarea" value={selected.internal_notes || ""} onChange={(e) => setSelected({ ...selected, internal_notes: e.target.value })} data-testid="enquiry-internal-notes" />
              </div>
              <div className="mt-4">
                <label className="label-mono opacity-60">Production notes (private)</label>
                <textarea rows={4} className="editorial-textarea" value={selected.production_notes || ""} onChange={(e) => setSelected({ ...selected, production_notes: e.target.value })} data-testid="enquiry-production-notes" />
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <button onClick={saveEnquiry} className="btn-solid" data-testid="enquiry-save">Save</button>
                <a
                  href={`mailto:${selected.email}?subject=Re: your enquiry (${selected.reference})`}
                  className="btn-outline-ink"
                  data-testid="enquiry-email"
                >
                  Email client
                </a>
                <a
                  href={`https://wa.me/${(selected.mobile || "").replace(/[^0-9]/g, "")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-outline-ink"
                  data-testid="enquiry-whatsapp"
                >
                  WhatsApp
                </a>
                <button onClick={() => del(selected)} className="btn-outline-ink text-red-700" data-testid="enquiry-delete">Delete</button>
              </div>
            </div>
          ) : (
            <p className="label-mono opacity-60">Select an enquiry to view details.</p>
          )}
        </div>
      </div>
    </div>
  );
}
